import { useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useUser } from '@/contexts/UserContext';
import { useSubscriptionProducts } from '@/contexts/SubscriptionProductsContext';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import { showErrorToast, TOAST_MESSAGES } from '@/utils/toasts';
import { APIRequestError } from '@/services/api';
import { getAllOwnedMessage, parseOptionalParam, parsePlanParams, startCheckoutChain } from '@/services/checkoutChain';

/**
 * Startet für einen eingeloggten User direkt den Stripe-Checkout für ein oder mehrere per
 * Query-Parameter vorausgewählte Pakete (und optionalen Coupon) und leitet ihn zur
 * Checkout-URL weiter. Ziel-Route: `/checkout?plan=X&coupon=Y` bzw. `/checkout?plan=X&plan=Z`.
 * Mehrere Pakete werden als verketteter Checkout nacheinander gebucht (siehe checkoutChain.ts).
 *
 * Ausgeloggte User erreichen diese Komponente nicht direkt – der AuthRouter
 * leitet sie zuvor über `/login?redirect=/checkout?...` und den Auto-Checkout
 * in `handleLogin` (App.tsx) hierher zurück.
 */
export default function CheckoutLauncher() {
  const { user, loading: userLoading } = useUser();
  const { products, loading: productsLoading } = useSubscriptionProducts();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const hasStartedRef = useRef(false);

  const loading = userLoading || productsLoading;

  useEffect(() => {
    // Erst handeln, wenn User- und Produkt-Context geladen sind – und nur einmal
    if (loading) {
      return;
    }
    if (hasStartedRef.current) {
      return;
    }
    hasStartedRef.current = true;

    const plans = parsePlanParams(searchParams, products);
    const coupon = parseOptionalParam(searchParams, 'coupon');
    const referrerCode = parseOptionalParam(searchParams, 'referrer');

    if (plans.length === 0) {
      showErrorToast('Ungültiges oder fehlendes Paket im Checkout-Link.');
      navigate('/pricing', { replace: true });
      return;
    }

    if (user === null) {
      // Sollte im AppRouter nicht auftreten (nur eingeloggt gerendert)
      navigate('/login', { replace: true });
      return;
    }

    const run = async () => {
      try {
        const result = await startCheckoutChain({
          user: user,
          plans: plans,
          coupon: coupon,
          referrerCode: referrerCode,
          source: 'checkout-link',
          products: products,
        });
        if (result === 'ALL_OWNED') {
          // Bereits aktive Pakete -> sofortiges Feedback statt Checkout
          showErrorToast(getAllOwnedMessage(plans.length));
          navigate('/dashboard', { replace: true });
        }
        // REDIRECTING -> Browser geht zu Stripe, Spinner absichtlich stehen lassen
      } catch (error) {
        console.error('Checkout via link failed:', error);
        // Backend kann kein Stripe-Produkt für dieses Paket auflösen (z.B. in dieser
        // Umgebung nicht konfiguriert / nicht verkäuflich) -> HTTP 500 "failed to get
        // Stripe product". Der errorcode ist das generische INTERNAL_ERROR, daher wird
        // hier auf die Meldung geprüft (analog zu den Message-Checks in Registration.tsx).
        if (error instanceof APIRequestError && error.message.toLowerCase().includes('stripe product')) {
          showErrorToast('Dieses Paket ist aktuell nicht verfügbar.');
          navigate('/pricing', { replace: true });
          return;
        }
        showErrorToast(TOAST_MESSAGES.GENERAL_ERROR);
        navigate('/pricing', { replace: true });
      }
    };

    void run();
  }, [loading, user, products, searchParams, navigate]);

  return <LoadingSpinner message="Du wirst zum Checkout weitergeleitet..." />;
}
